import { ReactElement } from 'react';
import { MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SEARCH_TYPES, SearchType, SearchTypeValue } from '../../constants/SearchType';
import './SearchTypeSelect.scss';

type SearchTypeSelectProps = {
  value: SearchTypeValue;
  onChange: (type: SearchTypeValue) => void;
  className?: string;
};

/** The label of each search type; the search page reuses them as headings. */
export const useSearchTypeLabels = (): Record<SearchTypeValue, string> => {
  const { t } = useTranslation();

  // Literal keys, so `yarn gen:i18n` finds them.
  return {
    [SearchType.All]: t('search.type.all'),
    [SearchType.Products]: t('search.type.products'),
    [SearchType.Services]: t('search.type.services'),
    [SearchType.Posts]: t('search.type.posts'),
    [SearchType.Specials]: t('search.type.specials'),
    [SearchType.Dates]: t('search.type.dates'),
    [SearchType.Jobs]: t('search.type.jobs'),
    [SearchType.Events]: t('search.type.events'),
    [SearchType.Vendors]: t('search.type.vendors')
  };
};

/** The green "what to search for" field next to a search input. */
export const SearchTypeSelect = ({
  value,
  onChange,
  className = ''
}: SearchTypeSelectProps): ReactElement => {
  const { t } = useTranslation();
  const labels = useSearchTypeLabels();

  return (
    <TextField
      select
      size='small'
      value={value}
      onChange={(event) => onChange(event.target.value as SearchTypeValue)}
      className={`search-type-select ${className}`}
      slotProps={{
        htmlInput: { 'aria-label': t('search.type.label') },
        select: { MenuProps: { className: 'search-type-select__menu' } }
      }}>
      {SEARCH_TYPES.map((type) => (
        <MenuItem key={type} value={type}>
          {labels[type]}
        </MenuItem>
      ))}
    </TextField>
  );
};
